import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassFormGroup,
  GlassCard,
} from '@/design-system';
import ComboBox, { ComboBoxOption } from '@/components/ui/ComboBox';
import CostProfileBuilder from '@/components/ui/CostProfileBuilder';
import type { Team, CostProfile, User, TeamCostAllocation, Program } from '@/types';
import type { AppDispatch } from '@/store';
import { fetchResourceLevels, selectActiveResourceLevels } from '@/store/slices/resourceLevelsSlice';

interface TeamFormProps {
  initialData?: Team | null;
  programId?: string;
  onSubmit: (data: Partial<Team>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  users?: User[];
  costProfiles?: CostProfile[];
  programs: Program[];
}

const TeamForm: React.FC<TeamFormProps> = ({
  initialData,
  programId,
  onSubmit,
  onCancel,
  isLoading = false,
  users: propUsers = [],
  costProfiles = [],
  programs,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const resourceLevels = useSelector(selectActiveResourceLevels);

  const [users, setUsers] = useState<User[]>(propUsers);
  const [manualEntries, setManualEntries] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    memberCount: 5,
    costProfileId: '',
    programId: programId || '',
    teamLeadId: undefined as string | undefined,
    teamLeadText: undefined as string | undefined,
    teamLeadType: 'USER' as 'USER' | 'TEXT',
    teamLeadResourceLevelId: undefined as string | undefined,
    costAllocations: [] as Array<{ costProfileId: string; resourceCount: number }>,
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [useFlexibleCostProfiles, setUseFlexibleCostProfiles] = useState(false);

  // Fetch resource levels on mount
  useEffect(() => {
    dispatch(fetchResourceLevels(undefined));
  }, [dispatch]);

  // Fetch users and manual entries with TEAM_LEAD_SCOPE on component mount
  useEffect(() => {
    const fetchTeamLeads = async () => {
      setUsersLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get('/api/v1/user-scopes/suggestions?scope=TEAM_LEAD_SCOPE', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const suggestions = response.data.data.suggestions || [];

          // Extract USER type suggestions and convert to User format
          const userSuggestions = suggestions
            .filter((s: any) => s.type === 'USER')
            .map((s: any) => ({
              id: s.id,
              firstName: s.label.split(' ')[0],
              lastName: s.label.split(' ').slice(1).join(' '),
              email: s.email
            }));

          // Extract manual TEXT entries
          const manualSuggestions = suggestions
            .filter((s: any) => s.type === 'TEXT')
            .map((s: any) => s.label);

          setUsers(userSuggestions);
          setManualEntries(manualSuggestions);
        }
      } catch (error) {
        console.error('Failed to fetch team leads:', error);
        // Fallback to prop users if API call fails
        if (propUsers.length > 0) {
          setUsers(propUsers);
        }
      } finally {
        setUsersLoading(false);
      }
    };

    fetchTeamLeads();
  }, []);

  useEffect(() => {
    if (initialData) {
      const hasFlexibleCosts = initialData.costAllocations && initialData.costAllocations.length > 0;
      setUseFlexibleCostProfiles(hasFlexibleCosts);

      setFormData({
        name: initialData.name,
        memberCount: initialData.memberCount,
        costProfileId: initialData.costProfileId,
        programId: initialData.programId,
        teamLeadId: initialData.teamLeadId,
        teamLeadText: initialData.teamLeadText,
        teamLeadType: initialData.teamLeadType,
        teamLeadResourceLevelId: initialData.teamLeadResourceLevelId,
        costAllocations: hasFlexibleCosts
          ? initialData.costAllocations!.map(ca => ({
              costProfileId: ca.costProfileId,
              resourceCount: ca.resourceCount,
            }))
          : [],
        isActive: initialData.isActive
      });
    } else if (!formData.programId && programs.length > 0) {
      setFormData(prev => ({ ...prev, programId: programs[0].id }));
    }
  }, [initialData, programs]);

  // Normalize the standard cost profiles and rates
  const standardRates = useMemo(
    () => ({
      onshore: 150,
      offshore: 27,
      blended: 89,
    }),
    []
  );

  const standardCostProfiles = useMemo(() => {
    const normalized = costProfiles
      .filter(cp => ['onshore', 'offshore', 'blended'].includes(cp.name.toLowerCase()))
      .map(cp => {
        const key = cp.name.toLowerCase();
        const enforcedRate = standardRates[key as keyof typeof standardRates];
        return { ...cp, hourlyRate: enforcedRate ?? cp.hourlyRate };
      });
    return normalized;
  }, [costProfiles, standardRates]);

  // When editing a team, ensure its current cost profile is always available in the dropdown
  const availableCostProfiles = useMemo(() => {
    const base = standardCostProfiles.length > 0 ? standardCostProfiles : costProfiles;

    // If editing a team with a non-standard cost profile, include it in the options
    if (initialData?.costProfileId) {
      const currentProfile = costProfiles.find(cp => cp.id === initialData.costProfileId);
      if (currentProfile && !base.some(cp => cp.id === currentProfile.id)) {
        return [...base, currentProfile];
      }
    }

    return base;
  }, [standardCostProfiles, costProfiles, initialData?.costProfileId]);

  useEffect(() => {
    // Only auto-select default cost profile for NEW teams (when initialData is not provided)
    if (!initialData && !useFlexibleCostProfiles && !formData.costProfileId && availableCostProfiles.length > 0) {
      setFormData(prev => ({ ...prev, costProfileId: availableCostProfiles[0].id }));
    }
  }, [initialData, availableCostProfiles, useFlexibleCostProfiles, formData.costProfileId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Team name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Team name must not exceed 100 characters';
    }

    if (!formData.memberCount || formData.memberCount < 1) {
      newErrors.memberCount = 'Team must have at least 1 member';
    } else if (formData.memberCount > 50) {
      newErrors.memberCount = 'Team cannot exceed 50 members';
    }

    if (!formData.programId) {
      newErrors.programId = 'Program is required';
    }

    if (useFlexibleCostProfiles) {
      const allocated = formData.costAllocations.reduce((sum, ca) => sum + (ca.resourceCount || 0), 0);
      if (allocated !== formData.memberCount) {
        newErrors.costAllocations = `Allocate all ${formData.memberCount} members across cost profiles`;
      }
      if (formData.costAllocations.length === 0) {
        newErrors.costAllocations = 'Add at least one cost allocation';
      }
    } else if (!formData.costProfileId) {
      newErrors.costProfileId = 'Cost profile is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      teamLeadResourceLevelId: formData.teamLeadResourceLevelId || undefined,
      costAllocations: useFlexibleCostProfiles ? formData.costAllocations : [],
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Prepare options - includes both users and manual entries
  const userOptions: ComboBoxOption[] = [
    // User suggestions
    ...users.map(user => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName}`,
      email: user.email,
    })),
    // Manual entry suggestions with indicator
    ...manualEntries.map(entry => ({
      id: entry, // Use the text itself as ID for manual entries
      label: entry,
      email: '(Previously used)',
    }))
  ];

  const costProfileOptions = [
    { value: '', label: 'Select cost profile' },
    ...availableCostProfiles.map(cp => ({
      value: cp.id,
      label: `${cp.name} ($${cp.hourlyRate}/hour)`,
    })),
  ];

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const handleTeamLeadChange = (value: string, type: 'USER' | 'TEXT') => {
    if (type === 'USER') {
      setFormData(prev => ({
        ...prev,
        teamLeadId: value,
        teamLeadText: undefined,
        teamLeadType: 'USER',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        teamLeadId: undefined,
        teamLeadText: value,
        teamLeadType: 'TEXT',
      }));
    }

    if (errors.teamLead) {
      setErrors(prev => ({ ...prev, teamLead: '' }));
    }
  };

  const getTeamLeadValue = () => {
    if (formData.teamLeadType === 'USER' && formData.teamLeadId) {
      return formData.teamLeadId;
    } else if (formData.teamLeadType === 'TEXT' && formData.teamLeadText) {
      return formData.teamLeadText;
    }
    return '';
  };

  const getRateForProfile = (costProfileId: string) => {
    const profile = availableCostProfiles.find(cp => cp.id === costProfileId);
    if (!profile) return 0;
    const nameKey = profile.name.toLowerCase();
    const standard = standardRates[nameKey as keyof typeof standardRates];
    return standard ?? profile.hourlyRate ?? 0;
  };

  const computeFlexibleSummary = () => {
    const totalResources = formData.costAllocations.reduce((sum, ca) => sum + (ca.resourceCount || 0), 0);
    const totalCostPerHour = formData.costAllocations.reduce((sum, ca) => {
      return sum + (getRateForProfile(ca.costProfileId) * (ca.resourceCount || 0));
    }, 0);
    const blendedRate = totalResources > 0 ? totalCostPerHour / totalResources : 0;
    return { totalResources, blendedRate };
  };

  const { totalResources, blendedRate } = computeFlexibleSummary();
  const weeklyCost = useFlexibleCostProfiles
    ? blendedRate * totalResources * 40
    : getRateForProfile(formData.costProfileId) * formData.memberCount * 40;
  const monthlyCost = weeklyCost * 4.33;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassFormGroup label="Team Name" required className="md:col-span-2">
          <GlassInput
            value={formData.name}
            onChange={(value) => handleInputChange('name', value)}
            error={errors.name}
            placeholder="e.g., Frontend Engineering Team"
            fullWidth
          />
        </GlassFormGroup>

        <ComboBox
          label="Team Lead"
          options={userOptions}
          value={getTeamLeadValue()}
          onChange={handleTeamLeadChange}
          placeholder="Select team lead or enter manually..."
          allowFreeText={true}
          helperText="Select a user or enter a name manually"
          error={errors.teamLead}
          fullWidth
        />

        {(formData.teamLeadId || formData.teamLeadText) && (
          <GlassFormGroup label="Lead Resource Level" helperText="Auto-adds lead to team roster at this level">
            <GlassSelect
              value={formData.teamLeadResourceLevelId || ''}
              onChange={(value) => handleInputChange('teamLeadResourceLevelId', value || undefined)}
              options={[
                { value: '', label: 'Select resource level (optional)' },
                ...resourceLevels.map(rl => ({
                  value: rl.id,
                  label: `${rl.code} - ${rl.displayName}`,
                })),
              ]}
              error={errors.teamLeadResourceLevelId}
              fullWidth
            />
          </GlassFormGroup>
        )}

        <GlassFormGroup label="Member Count" required helperText="Number of team members (including lead)">
          <GlassInput
            type="number"
            value={formData.memberCount.toString()}
            onChange={(value) => handleInputChange('memberCount', parseInt(value) || 0)}
            error={errors.memberCount}
            min={1}
            max={50}
            fullWidth
          />
        </GlassFormGroup>

        <GlassFormGroup label="Program" required helperText="Teams must be associated to a program">
          <GlassSelect
            value={formData.programId}
            onChange={(value) => handleInputChange('programId', value)}
            options={[
              { value: '', label: 'Select program' },
              ...programs.map(program => ({ value: program.id, label: program.name })),
            ]}
            error={errors.programId}
            fullWidth
          />
        </GlassFormGroup>

        <GlassFormGroup label="Status" helperText="Active teams can receive allocations">
          <GlassSelect
            value={formData.isActive.toString()}
            onChange={(value) => handleInputChange('isActive', value === 'true')}
            options={statusOptions}
            fullWidth
          />
        </GlassFormGroup>

        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-[var(--text)]">
              Cost Profile Configuration
            </label>
            <GlassButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setUseFlexibleCostProfiles(!useFlexibleCostProfiles)}
            >
              {useFlexibleCostProfiles ? 'Use Simple Profile' : 'Customize mix'}
            </GlassButton>
          </div>
          <p className="text-xs text-[var(--muted)]">
            Simple: choose Onshore ($150/resource), Offshore ($27/resource), or Blended ($89/resource). Customize: enter how many resources use each rate.
          </p>

          {useFlexibleCostProfiles ? (
            <CostProfileBuilder
              costProfiles={availableCostProfiles}
              totalMembers={formData.memberCount}
              initialAllocations={formData.costAllocations as any}
              onChange={(allocations) => {
                setFormData(prev => ({ ...prev, costAllocations: allocations }));
                setErrors(prev => ({ ...prev, costProfileId: '', costAllocations: '' }));
              }}
              error={errors.costAllocations}
            />
          ) : (
            <GlassSelect
              value={formData.costProfileId}
              onChange={(value) => handleInputChange('costProfileId', value)}
              options={costProfileOptions}
              error={errors.costProfileId}
              fullWidth
            />
          )}
        </div>
      </div>

      {/* Cost Calculation Preview */}
      {formData.memberCount > 0 && (!useFlexibleCostProfiles ? formData.costProfileId : totalResources > 0) && (
        <GlassCard variant="default" padding="md">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text)] mb-2">Planned Cost Preview</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-[var(--muted)]">Weekly:</span>
              <span className="ml-2 text-status-success font-semibold">
                ${weeklyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div>
              <span className="text-[var(--muted)]">Monthly:</span>
              <span className="ml-2 text-accent-blue font-semibold">
                ${monthlyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div>
              <span className="text-[var(--muted)]">Yearly:</span>
              <span className="ml-2 text-accent-violet font-semibold">
                ${(weeklyCost * 52).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
          {useFlexibleCostProfiles && (
            <p className="text-xs text-[var(--muted)] mt-2">
              Customize: enter how many resources use Onshore, Offshore, or Blended rates. Remaining headcount must be allocated.
            </p>
          )}
        </GlassCard>
      )}

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--glass-border)]">
        <GlassButton
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </GlassButton>
        <GlassButton
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} Team
        </GlassButton>
      </div>
    </form>
  );
};

export default TeamForm;
